'use client'

import React from 'react'
import { motion } from 'framer-motion'
import { Button } from '@sarkiai/ui'
import { Languages, Heart, Sparkles, ArrowRight } from 'lucide-react'
import Link from 'next/link'
import Image from 'next/image'
import { useTranslations } from '@/components/I18nProvider'

interface SarkiAIDetailProps {
  /** Custom CTA link (defaults to /sarkiai) */
  ctaLink?: string
  /** Custom CTA label key (defaults to sarkiai.meet) */
  ctaLabelKey?: string
}

export function SarkiAIDetail({ ctaLink = '/sarkiai', ctaLabelKey = 'sarkiai.meet' }: SarkiAIDetailProps) {
  const t = useTranslations('services')

  const values = [
    { icon: Languages, title: t('sarkiai.value1.title'), desc: t('sarkiai.value1.desc') },
    { icon: Heart, title: t('sarkiai.value2.title'), desc: t('sarkiai.value2.desc') },
    { icon: Sparkles, title: t('sarkiai.value3.title'), desc: t('sarkiai.value3.desc') },
  ]

  return (
    <section className="py-20 relative overflow-hidden bg-background px-4 lg:px-12">
      <div className="container mx-auto">
        <div className="flex flex-col lg:flex-row-reverse items-center gap-16">
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="flex-1 space-y-8 text-right lg:text-left"
          >
            <h2 className="text-2xl md:text-4xl uppercase tracking-tighter leading-tight lg:-ml-12 text-foreground">
              {t('sarkiai.hero.title')}
            </h2>

            <p className="text-md text-muted-foreground leading-relaxed">
              {t('sarkiai.hero.subtitle')}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-6 pt-4">
              {values.map((v, i) => (
                <div key={i} className="flex flex-row-reverse lg:flex-row gap-6 items-center group">
                  <div className="shrink-0">
                    <div className="w-14 h-14 rounded-2xl bg-transparent border border-black/10 dark:border-white/20 flex items-center justify-center shadow-lg shadow-black/5 transition-transform group-hover:scale-110">
                      <v.icon className="w-7 h-7 text-black dark:text-white" />
                    </div>
                  </div>
                  <div className="lg:text-left text-right space-y-1">
                    <h4 className="uppercase tracking-tight text-xl">{v.title}</h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">{v.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="pt-8 flex justify-end lg:justify-start">
              <Button asChild size="default" className="rounded-2xl px-8 h-12 text-xs bg-black text-white hover:bg-black/90 uppercase tracking-widest transition-all">
                <Link href={ctaLink}>
                  {t(ctaLabelKey)}
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </div>
          </motion.div>


          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="flex-1"
          >
            {/* 2x2 Image Grid */}
            <div className="grid grid-cols-2 gap-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1 }}
                className="aspect-square bg-muted/50 backdrop-blur-sm rounded-3xl p-6 flex items-center justify-center shadow-lg"
              >
                <Image
                  src="/sarki-ai-logo-dark.svg"
                  alt=""
                  width={150}
                  height={150}
                  className="w-full h-full object-contain"
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="aspect-square bg-muted/50 backdrop-blur-sm rounded-3xl p-6 flex items-center justify-center shadow-lg"
              >
                <Image
                  src="/assets/images/glass2/4644649.webp"
                  alt=""
                  width={150}
                  height={150}
                  className="w-full h-full object-contain"
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3 }}
                className="aspect-square bg-muted/50 backdrop-blur-sm rounded-3xl p-6 flex items-center justify-center shadow-lg"
              >
                <Image
                  src="/assets/images/glass2/4644635.webp"
                  alt=""
                  width={150}
                  height={150}
                  className="w-full h-full object-contain"
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.4 }}
                className="aspect-square bg-muted/50 backdrop-blur-sm rounded-3xl p-6 flex items-center justify-center shadow-lg"
              >
                <Image
                  src="/assets/images/glass2/4644652.webp"
                  alt=""
                  width={150}
                  height={150}
                  className="w-full h-full object-contain"
                />
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}
